import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, CircularProgress,
  IconButton, Tooltip, ToggleButtonGroup, ToggleButton,
  Divider, Chip, Dialog, DialogTitle, DialogContent,
  TextField, List, ListItem, ListItemText, InputAdornment, Button
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import SubwayIcon from '@mui/icons-material/Subway';
import TramIcon from '@mui/icons-material/Tram';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import LagbasLogo from '../components/LagbasLogo';
import {
  OFFICES,
  OFFICE_STOPS,
  getLineColor,
  fetchDepartures,
  fetchTrainDepartures,
  parseDepartures,
  parseTrainAnnouncements,
  searchLocation,
  officeFromBasort,
  parseLineLabel,
  categoryToIcon,
  getTypeLabel,
} from '../utils/departureApi';

// ── Hållplatssökare ───────────────────────────────────────────────────────────

function StopSearchDialog({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [copied, setCopied] = useState(null);
  const [searchError, setSearchError] = useState(null);

  async function handleSearch(q) {
    setQuery(q);
    setSearchError(null);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const locs = await searchLocation(q.trim());
      setResults(locs.slice(0, 12));
    } catch (e) {
      setResults([]);
      setSearchError(e.message);
    }
    finally { setSearching(false); }
  }

  function copyId(id) {
    navigator.clipboard.writeText(id).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Hitta hållplats-ID
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Sök på hållplatsnamn och kopiera ExtId till departureApi.js
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth size="small" sx={{ mb: 1 }}
          placeholder="Ex: Bergshamra, Korsvägen…"
          value={query}
          onChange={e => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
        />
        <List dense>
          {results.map((loc) => {
            const id = loc.extId || loc.id || '';
            return (
              <ListItem
                key={id}
                secondaryAction={
                  <Tooltip title={copied === id ? 'Kopierat!' : 'Kopiera ExtId'}>
                    <IconButton size="small" onClick={() => copyId(id)}>
                      <ContentCopyIcon sx={{ fontSize: '0.9rem', color: copied === id ? 'success.main' : 'inherit' }} />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={loc.name}
                  secondary={
                    <span>
                      <strong>{id}</strong>
                      {loc.Products?.Product?.[0]?.catOut && ` · ${loc.Products.Product[0].catOut}`}
                    </span>
                  }
                />
              </ListItem>
            );
          })}
          {searchError && (
            <ListItem><ListItemText secondary={<span style={{ color: '#dc2626' }}>Fel: {searchError}</span>} /></ListItem>
          )}
          {!searching && !searchError && query.length >= 2 && results.length === 0 && (
            <ListItem><ListItemText secondary="Inga resultat" /></ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}

// ── Hemhållplatser-dialog ─────────────────────────────────────────────────────

function iconFromCatOut(catOut) {
  const c = (catOut || '').toUpperCase();
  if (c === 'U') return 'metro';
  if (c === 'S') return 'tram';
  if (c === 'J' || c === 'F') return 'train';
  return 'bus';
}

function HomeStopsDialog({ open, onClose, homeCity, savedStops, onSave }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  useEffect(() => {
    if (open && homeCity && query === '') {
      doSearch(homeCity);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function doSearch(q) {
    setQuery(q);
    setSearchError(null);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const locs = await searchLocation(q.trim());
      setResults(locs.slice(0, 12));
    } catch (e) {
      setResults([]);
      setSearchError(e.message);
    }
    finally { setSearching(false); }
  }

  function addStop(loc) {
    const id = loc.extId || loc.id;
    if (!id || savedStops.some(s => s.id === id)) return;
    const catOut = loc.Products?.Product?.[0]?.catOut || '';
    onSave([...savedStops, { id, name: loc.name, icon: iconFromCatOut(catOut) }]);
  }

  function removeStop(id) {
    onSave(savedStops.filter(s => s.id !== id));
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Mina hemhållplatser
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Välj hållplatser nära din hemadress som visas i avgångstavlan
        </Typography>
      </DialogTitle>
      <DialogContent>
        {savedStops.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              SPARADE HÅLLPLATSER
            </Typography>
            <List dense>
              {savedStops.map(s => (
                <ListItem key={s.id}
                  sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 0.5 }}
                  secondaryAction={
                    <IconButton size="small" onClick={() => removeStop(s.id)}>
                      <CloseIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={s.name}
                    secondary={s.id}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <TextField
          fullWidth size="small" sx={{ mb: 1 }}
          placeholder={`Sök, t.ex. ${homeCity || 'din stad'}…`}
          value={query}
          onChange={e => doSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
        />
        <List dense>
          {results.map(loc => {
            const id = loc.extId || loc.id || '';
            const already = savedStops.some(s => s.id === id);
            return (
              <ListItem key={id}
                secondaryAction={
                  <IconButton size="small" onClick={() => addStop(loc)} disabled={already}>
                    {already
                      ? <CheckIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
                      : <AddIcon sx={{ fontSize: '0.9rem' }} />}
                  </IconButton>
                }
              >
                <ListItemText
                  primary={loc.name}
                  secondary={
                    <span>
                      <strong>{id}</strong>
                      {loc.Products?.Product?.[0]?.catOut && ` · ${loc.Products.Product[0].catOut}`}
                    </span>
                  }
                />
              </ListItem>
            );
          })}
          {searchError && (
            <ListItem><ListItemText secondary={<span style={{ color: '#dc2626' }}>Fel: {searchError}</span>} /></ListItem>
          )}
          {!searching && !searchError && query.length >= 2 && results.length === 0 && (
            <ListItem><ListItemText secondary="Inga resultat" /></ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}

// ── Hjälpkomponenter ──────────────────────────────────────────────────────────

function getModeIcon(icon, sz = '1.1rem') {
  if (icon === 'metro') return <SubwayIcon sx={{ fontSize: sz }} />;
  if (icon === 'bus') return <DirectionsBusIcon sx={{ fontSize: sz }} />;
  if (icon === 'tram') return <TramIcon sx={{ fontSize: sz }} />;
  if (icon === 'ferry') return <DirectionsBoatIcon sx={{ fontSize: sz }} />;
  return <TrainIcon sx={{ fontSize: sz }} />;
}

function MinBadge({ mins, large = false, walkMins = null }) {
  const isNow = mins <= 0;
  // Ej hinnbar: positiv tid men kortare än gångavståndet
  const isMissed = walkMins != null && mins > 0 && mins < walkMins;
  const clockStr = new Date(Date.now() + mins * 60000)
    .toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  return (
    <Tooltip title={clockStr} placement="top" arrow>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '1px',
        bgcolor: isNow ? '#DC2626' : '#1e293b',
        borderRadius: '4px',
        px: large ? 0.9 : 0.7, height: large ? 30 : 24,
        color: '#fff',
        opacity: isMissed ? 0.3 : 1,
        cursor: 'default',
      }}>
        <Typography sx={{
          fontSize: large ? '0.9rem' : '0.78rem',
          fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {isNow ? 'Nu' : mins}
        </Typography>
        {!isNow && (
          <Typography sx={{ fontSize: large ? '0.62rem' : '0.57rem', opacity: 0.7, lineHeight: 1, ml: '1px' }}>
            min
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function DelayBadge({ delay }) {
  if (delay < 2) return null;
  return (
    <Typography sx={{ fontSize: '0.68rem', color: '#F97316', fontWeight: 700, ml: 0.5 }}>
      +{delay}
    </Typography>
  );
}

// ── En avgångsrad ─────────────────────────────────────────────────────────────

function DepartureRow({ group, compact = false, dark = false, onFocusLine, walkMins = null, maxTimes = 3 }) {
  const textColor = dark ? '#cbd5e1' : 'text.primary';
  const sz = compact ? '0.78rem' : '0.85rem';
  const { line, trainType } = parseLineLabel(group.lineNumber);
  const typeLabel = trainType || getTypeLabel(group.category);
  const lineColor = getLineColor(line, group.category);
  const modeIcon = getModeIcon(categoryToIcon(group.category), compact ? '0.85rem' : '0.95rem');

  return (
    <Box
      onClick={onFocusLine ? () => onFocusLine(group) : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        mb: compact ? 0.6 : 0.8, minWidth: 0,
        cursor: onFocusLine ? 'pointer' : 'default',
        '&:hover': onFocusLine ? { opacity: 0.8 } : {},
      }}
    >
      {/* Trafikslags-ikon */}
      <Box sx={{ width: 18, flexShrink: 0, display: 'flex', justifyContent: 'center', color: dark ? '#475569' : '#94a3b8' }}>
        {modeIcon}
      </Box>

      {/* Typ-chip (T-bana, Spårvagn, Buss, Pendeltåg …) – fast bredd för att linjechip ska aligna */}
      <Box sx={{
        width: '76px', height: 22, borderRadius: '4px',
        bgcolor: '#334155',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, overflow: 'hidden',
      }}>
        <Typography sx={{ color: '#cbd5e1', fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {typeLabel}
        </Typography>
      </Box>

      {/* Tågnummer / linjechip (färgad) */}
      <Box sx={{
        minWidth: 28, height: 22, px: 0.7, borderRadius: '4px',
        bgcolor: lineColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 800 }}>
          {line}
        </Typography>
      </Box>

      {/* Riktning */}
      <Typography sx={{
        flex: 1, fontSize: sz, color: textColor,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {group.direction}
      </Typography>


      {/* Tider */}
      <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0, alignItems: 'center' }}>
        {group.departures.slice(0, maxTimes).map((m, i) => (
          <MinBadge key={i} mins={m} walkMins={walkMins} />
        ))}
        {group.isDelayed && <DelayBadge delay={group.delay} />}
      </Box>
    </Box>
  );
}

// ── Hållplatskort ─────────────────────────────────────────────────────────────

function StopCard({ stop, refreshTick, dark = false, onFocusStop, onFocusLine, focusedLine, focused = false }) {
  const [groups, setGroups] = useState([]);
  const [trainGroups, setTrainGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rawDeps, rawTrains] = await Promise.all([
        fetchDepartures(stop.id),
        stop.tvStation ? fetchTrainDepartures(stop.tvStation) : Promise.resolve([]),
      ]);
      setGroups(parseDepartures(rawDeps));
      setTrainGroups(parseTrainAnnouncements(rawTrains));
      setUpdatedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [stop.id, stop.tvStation]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const timeStr = updatedAt
    ? updatedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
    : null;

  // Om fokusläge – visa bara matchande linje
  const displayGroups = focusedLine
    ? groups.filter(g => g.lineNumber === focusedLine.lineNumber && g.direction === focusedLine.direction)
    : groups;
  const displayTrains = focusedLine
    ? trainGroups.filter(g => g.lineNumber === focusedLine.lineNumber || g.trainId === focusedLine.trainId)
    : trainGroups;

  // Riktningsfilter (t.ex. Stockholm C → bara mot Göteborg)
  const df = stop.directionFilter;
  const filteredGroups = df
    ? displayGroups.filter(g => g.direction.toLowerCase().includes(df))
    : displayGroups;
  const filteredTrains = df
    ? displayTrains.filter(g => g.direction.toLowerCase().includes(df))
    : displayTrains;

  const allGroups = [...filteredGroups, ...filteredTrains];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0, overflow: 'hidden',
        borderColor: dark ? '#1e293b' : 'divider',
        bgcolor: dark ? '#0f172a' : 'background.paper',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.2,
        bgcolor: dark ? '#1e293b' : 'background.default',
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '1px solid', borderColor: dark ? '#334155' : 'divider',
        cursor: onFocusStop ? 'pointer' : 'default',
      }}
        onClick={onFocusStop ? () => onFocusStop(stop) : undefined}
      >
        <Box sx={{ color: dark ? '#94a3b8' : 'text.secondary', display: 'flex' }}>
          {getModeIcon(stop.icon)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2, color: dark ? '#f1f5f9' : 'text.primary' }}>
            {stop.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.7rem', color: dark ? '#64748b' : 'text.secondary' }}>
              {stop.subtitle}{timeStr && ` · ${timeStr}`}
            </Typography>
            {stop.walkMins != null && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: '2px',
                color: dark ? '#475569' : 'text.disabled',
              }}>
                <DirectionsWalkIcon sx={{ fontSize: '0.75rem' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                  {stop.walkMins} min
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {loading && <CircularProgress size={14} sx={dark ? { color: '#475569' } : {}} />}
        {onFocusStop && !loading && (
          <ArrowForwardIcon sx={{ fontSize: '0.9rem', color: dark ? '#475569' : 'text.disabled' }} />
        )}
      </Box>

      {/* Avgångsrader */}
      <Box sx={{ px: 2, py: 1, flex: 1 }}>
        {error && (
          <Typography sx={{ fontSize: '0.78rem', color: 'error.main' }}>{error}</Typography>
        )}
        {!loading && !error && allGroups.length === 0 && (
          <Typography sx={{ fontSize: '0.78rem', color: dark ? '#475569' : 'text.secondary' }}>
            Inga avgångar inom 2 h
          </Typography>
        )}
        {allGroups.slice(0, focused ? 20 : (stop.maxRows || 8)).map((g, i) => (
          <DepartureRow
            key={i}
            group={g}
            compact={!focused}
            dark={dark}
            onFocusLine={onFocusLine}
            walkMins={stop.walkMins}
            maxTimes={focusedLine ? 6 : focused ? 4 : 3}
          />
        ))}
      </Box>
    </Paper>
  );
}

// ── Klocka ────────────────────────────────────────────────────────────────────

function DigitalClock({ dark = true }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <Typography sx={{
      fontSize: '2rem', fontWeight: 900,
      color: dark ? '#f1f5f9' : 'text.primary',
      fontVariantNumeric: 'tabular-nums', letterSpacing: 2,
    }}>
      {time.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
    </Typography>
  );
}

// ── Jättestor minutbadge för fullskärm ───────────────────────────────────────

function GiantMinBadge({ mins }) {
  const isNow = mins <= 0;
  const clockStr = new Date(Date.now() + mins * 60000)
    .toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  return (
    <Tooltip title={clockStr} placement="top" arrow>
      <Box sx={{
        display: 'flex', alignItems: 'baseline', gap: '3px',
        bgcolor: isNow ? '#DC2626' : '#1e293b',
        borderRadius: '6px',
        px: '0.6em', py: '0.15em',
        color: '#fff',
        minWidth: '2.4em',
        justifyContent: 'center',
      }}>
        <Typography sx={{
          fontSize: 'clamp(1.8rem, 3.5vw, 5rem)',
          fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {isNow ? 'Nu' : mins}
        </Typography>
        {!isNow && (
          <Typography sx={{ fontSize: 'clamp(0.9rem, 1.5vw, 2.2rem)', opacity: 0.65, lineHeight: 1 }}>
            min
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// ── Jättestor avgångsrad för fullskärm ───────────────────────────────────────

function GiantDepartureRow({ group, isLast }) {
  const { line, trainType } = parseLineLabel(group.lineNumber);
  const typeLabel = trainType || getTypeLabel(group.category);
  const lineColor = getLineColor(line, group.category);
  const modeIcon = getModeIcon(categoryToIcon(group.category), 'clamp(1.5rem, 2.5vw, 3.5rem)');

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '1.5vw',
      py: '0.6vh',
      borderBottom: isLast ? 'none' : '1px solid #1e293b',
    }}>
      {/* Trafikslags-ikon */}
      <Box sx={{ color: '#475569', display: 'flex', flexShrink: 0 }}>
        {modeIcon}
      </Box>

      {/* Typ-etikett */}
      <Box sx={{
        bgcolor: '#1e293b', borderRadius: '4px',
        px: '0.5em', py: '0.1em',
        flexShrink: 0,
      }}>
        <Typography sx={{
          color: '#94a3b8',
          fontSize: 'clamp(0.8rem, 1.4vw, 2rem)',
          fontWeight: 700, whiteSpace: 'nowrap',
        }}>
          {typeLabel}
        </Typography>
      </Box>

      {/* Linjechip */}
      <Box sx={{
        bgcolor: lineColor, borderRadius: '4px',
        px: '0.5em', py: '0.1em',
        flexShrink: 0, minWidth: '2em',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{
          color: '#fff',
          fontSize: 'clamp(1rem, 2vw, 2.8rem)',
          fontWeight: 900,
        }}>
          {line}
        </Typography>
      </Box>

      {/* Riktning */}
      <Typography sx={{
        flex: 1,
        fontSize: 'clamp(1.4rem, 2.8vw, 4rem)',
        fontWeight: 700, color: '#f1f5f9',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {group.direction}
      </Typography>

      {/* Tider */}
      <Box sx={{ display: 'flex', gap: '0.5vw', flexShrink: 0, alignItems: 'center' }}>
        {group.departures.slice(0, 3).map((m, i) => (
          <GiantMinBadge key={i} mins={m} />
        ))}
        {group.isDelayed && (
          <Typography sx={{ fontSize: 'clamp(0.9rem, 1.5vw, 2rem)', color: '#F97316', fontWeight: 700 }}>
            +{group.delay}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Jättestor hållplatstavla för fullskärm ────────────────────────────────────

function GiantStopBoard({ stop, refreshTick }) {
  const [groups, setGroups] = useState([]);
  const [trainGroups, setTrainGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rawDeps, rawTrains] = await Promise.all([
        fetchDepartures(stop.id),
        stop.tvStation ? fetchTrainDepartures(stop.tvStation) : Promise.resolve([]),
      ]);
      setGroups(parseDepartures(rawDeps));
      setTrainGroups(parseTrainAnnouncements(rawTrains));
      setUpdatedAt(new Date());
    } catch (_) {}
    finally { setLoading(false); }
  }, [stop.id, stop.tvStation]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const df = stop.directionFilter;
  const allGroups = [
    ...(df ? groups.filter(g => g.direction.toLowerCase().includes(df)) : groups),
    ...(df ? trainGroups.filter(g => g.direction.toLowerCase().includes(df)) : trainGroups),
  ].slice(0, 10);

  const timeStr = updatedAt
    ? updatedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: '3vw', py: '2vh', overflow: 'hidden' }}>
      {/* Hållplatsnamn */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1vw', mb: '1.5vh', flexShrink: 0 }}>
        <Box sx={{ color: '#3b82f6', display: 'flex' }}>
          {getModeIcon(stop.icon, 'clamp(2rem, 4vw, 6rem)')}
        </Box>
        <Box>
          <Typography sx={{
            fontSize: 'clamp(2rem, 5vw, 7rem)',
            fontWeight: 900, color: '#f1f5f9', lineHeight: 1,
          }}>
            {stop.name}
          </Typography>
          {stop.subtitle && (
            <Typography sx={{ fontSize: 'clamp(0.9rem, 1.5vw, 2rem)', color: '#475569' }}>
              {stop.subtitle}{timeStr && ` · uppdaterat ${timeStr}`}
            </Typography>
          )}
        </Box>
        {loading && <CircularProgress size={24} sx={{ color: '#334155', ml: 1 }} />}
      </Box>

      <Divider sx={{ borderColor: '#1e293b', mb: '1vh', flexShrink: 0 }} />

      {/* Avgångsrader */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
        {allGroups.length === 0 && !loading && (
          <Typography sx={{ fontSize: 'clamp(1.5rem, 3vw, 4rem)', color: '#334155' }}>
            Inga avgångar inom 2 h
          </Typography>
        )}
        {allGroups.map((g, i) => (
          <GiantDepartureRow key={i} group={g} isLast={i === allGroups.length - 1} />
        ))}
      </Box>
    </Box>
  );
}

// ── Fullskärmsvy ──────────────────────────────────────────────────────────────

function FullscreenView({ office, stops, focusedStop, onExit }) {
  const [idx, setIdx] = useState(
    focusedStop ? stops.findIndex(s => s.id === focusedStop.id) : 0
  );
  const [refreshTick, setRefreshTick] = useState(0);
  const [paused, setPaused] = useState(!!focusedStop);

  // Auto-carousel var 10s, pausas vid interaktion
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx(i => (i + 1) % stops.length), 10000);
    return () => clearInterval(t);
  }, [paused, stops.length]);

  // Refresh var 30s
  useEffect(() => {
    const t = setInterval(() => setRefreshTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  function handleStopClick(i) {
    setIdx(i);
    setPaused(true);
  }

  const stop = stops[idx];

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1400,
      bgcolor: '#0f172a',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header – kompakt */}
      <Box sx={{
        px: '2vw', py: '1vh',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #1e293b', flexShrink: 0,
      }}>
        <Typography sx={{ fontWeight: 900, fontSize: 'clamp(0.7rem, 1.2vw, 1.4rem)', color: '#334155', letterSpacing: 2 }}>
          AVGÅNGSTAVLA · {OFFICES[office]?.displayName?.toUpperCase() || ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {stops.map((s, i) => (
            <Tooltip key={i} title={s.name}>
              <Box
                onClick={() => handleStopClick(i)}
                sx={{
                  width: i === idx ? 24 : 8, height: 8,
                  borderRadius: '4px',
                  bgcolor: i === idx ? '#3b82f6' : '#1e293b',
                  cursor: 'pointer', transition: 'all 0.3s',
                }}
              />
            </Tooltip>
          ))}
          {paused && (
            <Chip
              label="Auto"
              size="small"
              onClick={() => setPaused(false)}
              sx={{ bgcolor: '#1e293b', color: '#64748b', fontSize: '0.65rem', height: 20, cursor: 'pointer' }}
            />
          )}
          <DigitalClock />
          <IconButton onClick={onExit} sx={{ color: '#475569', ml: 1 }}>
            <FullscreenExitIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Jättestor hållplatstavla */}
      <GiantStopBoard stop={stop} refreshTick={refreshTick} />
    </Box>
  );
}

// ── Huvud-komponent ───────────────────────────────────────────────────────────

export default function Avgangstavla() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageRef = useRef(null);

  const [office, setOffice] = useState('stockholm');
  const [mode, setMode] = useState('office');       // 'office' | 'home'
  const [homeStops, setHomeStops] = useState([]);   // [{id, name, icon}]
  const [homeCity, setHomeCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [savingOffice, setSavingOffice] = useState(false);
  const [stopSearchOpen, setStopSearchOpen] = useState(false);
  const [homeStopsOpen, setHomeStopsOpen] = useState(false);
  // Fokusläge
  const [focusedStop, setFocusedStop] = useState(null);
  const [focusedLine, setFocusedLine] = useState(null);

  // Hämta kontorspreferens
  useEffect(() => {
    async function load() {
      try {
        if (currentUser) {
          const snap = await getDoc(doc(db, 'users', currentUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            // MS-inloggad utan office – föreslå att sätta det i profilen
            // Prioritet: sparat override → härledd från basort → localStorage
            if (data.office) {
              setOffice(data.office);
            } else {
              const derived = officeFromBasort(data.basort);
              if (derived) {
                setOffice(derived);
              } else {
                const sessionOffice = localStorage.getItem('avgangstavla_office');
                if (sessionOffice) setOffice(sessionOffice);
              }
            }
            if (Array.isArray(data.homeStops)) setHomeStops(data.homeStops);
            if (data.homeCity) setHomeCity(data.homeCity);
          }
        }
      } catch (e) {
        console.error('Avgångstavla load error:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentUser]);

  // Auto-refresh var 30s
  useEffect(() => {
    const t = setInterval(() => setRefreshTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  async function handleOfficeChange(_, newOffice) {
    if (!newOffice || savingOffice) return;
    setSavingOffice(true);
    setOffice(newOffice);
    localStorage.setItem('avgangstavla_office', newOffice);
    try {
      if (currentUser) {
        await updateDoc(doc(db, 'users', currentUser.uid), { office: newOffice });
      }
    } catch (e) {
      console.error('Save office error:', e);
    } finally {
      setSavingOffice(false);
    }
  }

  async function handleSaveHomeStops(newStops) {
    setHomeStops(newStops);
    try {
      if (currentUser) {
        await updateDoc(doc(db, 'users', currentUser.uid), { homeStops: newStops });
      }
    } catch (e) {
      console.error('Save homeStops error:', e);
    }
  }

  // Auto-kiosk om ?kiosk=1 i URL
  useEffect(() => {
    if (searchParams.get('kiosk') === '1' && !loading) {
      setFullscreen(true);
      if (pageRef.current?.requestFullscreen) {
        pageRef.current.requestFullscreen().catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  function handleFullscreen() {
    setFullscreen(true);
    if (pageRef.current?.requestFullscreen) {
      pageRef.current.requestFullscreen().catch(() => {});
    }
  }

  function handleExitFullscreen() {
    setFullscreen(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }

  useEffect(() => {
    function onFsChange() {
      if (!document.fullscreenElement) setFullscreen(false);
    }
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const stops = mode === 'home'
    ? homeStops.map(s => ({ ...s, subtitle: homeCity || 'Hem', walkMins: null }))
    : (OFFICE_STOPS[office] || []);
  const officeName = mode === 'home'
    ? (homeCity || 'Hem')
    : (OFFICES[office]?.displayName || 'Stockholm');

  // Fokuserade hållplatser: filtrera om fokusläge
  const visibleStops = focusedStop ? stops.filter(s => s.id === focusedStop.id) : stops;

  return (
    <Box ref={pageRef} sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        {(focusedStop || focusedLine) ? (
          <IconButton color="inherit" size="small" onClick={() => { setFocusedStop(null); setFocusedLine(null); }}>
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Avgångstavla
            {focusedStop && ` · ${focusedStop.name}`}
            {focusedLine && ` · Linje ${focusedLine.lineNumber}`}
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {officeName}{mode === 'office' && OFFICES[office]?.address ? ` · ${OFFICES[office].address}` : ''}
          </Typography>
        </Box>

        {/* Lägesväljare: kontor (Stockholm/Göteborg) och Hem */}
        {!focusedStop && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ToggleButtonGroup
              value={mode === 'home' ? 'home' : office}
              exclusive
              onChange={(_, val) => {
                if (!val) return;
                if (val === 'home') {
                  setMode('home');
                  if (homeStops.length === 0) setHomeStopsOpen(true);
                } else {
                  setMode('office');
                  handleOfficeChange(null, val);
                }
              }}
              size="small"
              sx={{
                '.MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.2)',
                  fontSize: '0.75rem', py: 0.4, px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: '#FF6B00', color: '#fff', borderColor: '#FF6B00',
                    '&:hover': { bgcolor: '#e05e00' },
                  },
                },
              }}
            >
              {Object.entries(OFFICES).map(([key, val]) => (
                <ToggleButton key={key} value={key}>{val.displayName}</ToggleButton>
              ))}
              <ToggleButton value="home">Hem</ToggleButton>
            </ToggleButtonGroup>
            {mode === 'home' && (
              <Tooltip title="Redigera hemhållplatser">
                <IconButton color="inherit" size="small" onClick={() => setHomeStopsOpen(true)}>
                  <TuneIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        <Tooltip title="Hitta hållplats-ID">
          <IconButton color="inherit" size="small" onClick={() => setStopSearchOpen(true)}>
            <TuneIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Uppdatera">
          <IconButton color="inherit" size="small" onClick={() => setRefreshTick(n => n + 1)}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Öppnar helskärmsläge med maximalt stora avgångstider – lämpligt för att visa på en skärm i lokalen" arrow>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FullscreenIcon />}
            onClick={handleFullscreen}
            sx={{
              color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              fontSize: '0.75rem', whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            Kiosk-läge
          </Button>
        </Tooltip>
      </Box>

      <StopSearchDialog open={stopSearchOpen} onClose={() => setStopSearchOpen(false)} />
      <HomeStopsDialog
        open={homeStopsOpen}
        onClose={() => setHomeStopsOpen(false)}
        homeCity={homeCity}
        savedStops={homeStops}
        onSave={handleSaveHomeStops}
      />

      {/* Fullskärmsvy */}
      {fullscreen && (
        <FullscreenView
          office={office}
          stops={visibleStops.length > 0 ? visibleStops : stops}
          focusedStop={focusedStop}
          onExit={handleExitFullscreen}
        />
      )}

      {/* Innehåll */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Brödsmula i fokusläge */}
            {(focusedStop || focusedLine) && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {focusedStop && (
                  <Chip
                    label="← Alla hållplatser"
                    size="small"
                    onClick={() => { setFocusedStop(null); setFocusedLine(null); }}
                    sx={{ cursor: 'pointer' }}
                  />
                )}
                {focusedLine && (
                  <Chip
                    label={`${focusedLine.lineNumber} ×`}
                    size="small"
                    onClick={() => setFocusedLine(null)}
                    sx={{ cursor: 'pointer', bgcolor: getLineColor(focusedLine.lineNumber, focusedLine.category), color: '#fff' }}
                  />
                )}
              </Box>
            )}

            {mode === 'home' && stops.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Inga hemhållplatser sparade ännu.
                </Typography>
                <Chip
                  label="Lägg till hållplatser"
                  onClick={() => setHomeStopsOpen(true)}
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            )}

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}>
              {visibleStops.map(stop => (
                <StopCard
                  key={stop.id}
                  stop={stop}
                  refreshTick={refreshTick}
                  onFocusStop={!focusedStop ? setFocusedStop : null}
                  onFocusLine={setFocusedLine}
                  focused={!!focusedStop}
                  focusedLine={focusedLine}
                />
              ))}
            </Box>
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Minuter till avgång · Rött = avgår nu · Orange = försenad · Grå tider = hinner ej gå ·
            Data: ResRobot (SL/Västtrafik) + Trafikverket (tåg)
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
